import {useInput} from '../../../hooks/useInput';
import styles from './loginForm.module.css';
import LoadingIndicator from '../LoadingIndicator';
import React, {useRef, useState} from 'react';
import {useQuery} from '../../../hooks/useQuery';
import useIsMobile from '../../../hooks/useIsMobile';
import {
    Button,
    Input,
    Typography,
} from '@toppy-webshop/toppy-design-system/dist';
import {useI18nContext} from '../../../i18n/i18n-react';
import {useAuthStore} from '../../../store/authStore';

type LoginFormProps = {
    handleSubmit: (orderNumber: string, email: string) => Promise<void>;
    headerText: string;
};

const LoginForm = ({handleSubmit, headerText}: LoginFormProps) => {
    const {LL} = useI18nContext();
    const query = useQuery();
    const queryOrderNumber = query.get('order');
    const queryEmail = query.get('email');

    const {value: orderNumber, bind: bindOrderNumber} = useInput(
        queryOrderNumber ?? ''
    );
    const {value: email, bind: bindEmail} = useInput(queryEmail ?? '');
    const invalidBearerReason = useAuthStore(
        state => state.invalidBearerReason
    );

    const loginForm = useRef<HTMLFormElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(
        invalidBearerReason === null ? null : LL.auth.error[invalidBearerReason]
    );

    const {isMobile} = useIsMobile(576);

    const doSubmit = (
        e:
            | React.FormEvent<HTMLFormElement>
            | React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): void => {
        e.preventDefault();

        if (loginForm.current !== null && !loginForm.current.checkValidity()) {
            loginForm.current.reportValidity();
            setErrorMessage('Ongeldige invoer');
            return;
        }

        setErrorMessage(null);

        setIsLoading(true);
        handleSubmit(orderNumber, email)
            .then(() => {
                setIsLoading(false);
                setErrorMessage(null);
            })
            .catch((e: Error) => {
                setIsLoading(false);
                setErrorMessage(e.message);
            });
    };
    return (
        <>
            <div className={styles.header}>
                <div className={styles.title}>
                    <Typography variant={isMobile ? 'h3' : 'h2'}>
                        {headerText}
                    </Typography>
                </div>
            </div>
            <div className={styles.container}>
                <form
                    ref={loginForm}
                    className={styles.form}
                    onSubmit={doSubmit}
                >
                    <div className={styles.order}>
                        <Input
                            type={'text'}
                            placeholder={LL.login.orderNumber()}
                            isDisabled={false}
                            autocomplete={'off'}
                            labelText={LL.login.orderNumber()}
                            isRequired={true}
                            pattern="^[0-9]+$"
                            bindToInput={bindOrderNumber}
                        />
                    </div>

                    <div className={styles.email}>
                        <Input
                            type={'email'}
                            placeholder={LL.login.email()}
                            isDisabled={false}
                            autocomplete={'off'}
                            labelText={LL.login.email()}
                            isRequired={true}
                            bindToInput={bindEmail}
                        />
                    </div>
                    <div className={styles.button}>
                        <Button
                            type={'primary'}
                            label={'Zoeken'}
                            onClick={doSubmit}
                            full={isMobile}
                        />
                    </div>
                </form>
                {isLoading && <LoadingIndicator />}
                {errorMessage !== null && (
                    <p className={styles['error-message']}>{errorMessage}</p>
                )}
            </div>
        </>
    );
};

export default LoginForm;
